import { useNetwork } from '../stores/network';
import { useSync } from '../services/sync';
import { useT } from '../i18n';

/** Global connectivity + sync banner. Never lets the user think a run was lost. */
export function StatusBar() {
  const online = useNetwork((s) => s.online);
  const apiDown = useNetwork((s) => s.apiDown);
  const { pending, syncing, lastError } = useSync();
  const t = useT();

  if (!online) {
    return (
      <Banner tone="warn" testId="offline-banner">
        <strong>{t('OFFLINE')}</strong> · {t('Run data is saved on this device and will sync when the connection returns.')}
        {pending ? ` (${t(pending > 1 ? '{n} runs waiting' : '{n} run waiting', { n: pending })})` : ''}
      </Banner>
    );
  }
  if (apiDown) {
    return (
      <Banner tone="warn" testId="server-banner">
        <strong>{t('SERVER UNREACHABLE')}</strong> · {t('Runs are kept locally and retried automatically.')}
      </Banner>
    );
  }
  if (pending > 0) {
    return (
      <Banner tone="info" testId="sync-banner">
        {t(syncing ? (pending > 1 ? 'Syncing {n} runs' : 'Syncing {n} run') : pending > 1 ? 'Waiting to sync {n} runs' : 'Waiting to sync {n} run', { n: pending })}…{lastError ? ` (${lastError})` : ''}
      </Banner>
    );
  }
  return null;
}

function Banner({ tone, children, testId }: { tone: 'warn' | 'info'; children: React.ReactNode; testId: string }) {
  const cls = tone === 'warn' ? 'bg-warn text-ink-950' : 'bg-sky/90 text-ink-950';
  return (
    <div className={`fixed inset-x-0 top-0 z-40 px-4 pb-1.5 pt-[calc(var(--safe-top)+6px)] text-center text-xs font-semibold ${cls}`} role="status" data-testid={testId}>
      {children}
    </div>
  );
}
